import { AdminLocationField } from "@/admin/AdminLocationField.tsx";
import { AFTextField } from "@/admin/form/AdminFormFields.tsx";
import { WAdminModalCrudForm } from "@/admin/form/AdminModalContent.tsx";
import { ATextArea } from "@/admin/form/Fields.tsx";
import { UuidField } from "@/admin/form/UuidField.tsx";
import { AdminMetalOsSelect } from "@/admin/metal/AdminMetalOsSelect.tsx";
import { machineItemFields } from "@/admin/metal/machine/machineItemFields.ts";
import { machineCreateMutation, machineUpdateMutation } from "@/admin/metal/machine/machinesQuery.ts";
import type { AdminMachineWithOs } from "@/admin/metal/machine/machinesWithOs.ts";
import { ASpecSelect } from "@/admin/metal/specs/SpecSelect.tsx";
import { controllerProps, formPropsToFormFieldProps } from "@/components/forms/ariaController.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { LocationInputs } from "@/modules/location/LocationField.tsx";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { changedFields } from "@/utils/changedFields.ts";
import type { AdminMetalMachineCreateBody, MetalStateHistory } from "@warrenio/api-spec/spec.oats.gen";
import { Controller, useForm } from "react-hook-form";
import { keys, pick } from "remeda";

export function MachineItemModal({ item }: { item?: AdminMachineWithOs | undefined }) {
    interface Inputs extends AdminMetalMachineCreateBody, LocationInputs {
        os_id: MetalStateHistory["os_id"];
    }

    //#region Hooks
    const locations = useLocationsForType("bare_metal");

    const createFields: Inputs = {
        location: locations.defaultLocation,

        label: "",
        os_id: null,
        admin_notes: "",
        ssh_credentials: null,
        // NB: TypeScript hack, there's a validator attached to this field to make sure it's set before submit
        spec_uuid: undefined!,
        mac_addresses: [],
        ip_public_v4: "",
        ip_public_v6: null,
    };

    /** Existing item converted to form fields */
    const updateFields = item
        ? {
              ...item,
              // NB: This field is in a different path in the read vs. write API
              spec_uuid: item.spec.uuid,
          }
        : undefined;

    const form = useForm<Inputs>({
        defaultValues: updateFields ? pick(updateFields, keys(createFields)) : createFields,
    });
    const { control } = form;

    const createMutation = useStandardMutation(machineCreateMutation);
    const updateMutation = useStandardMutation(machineUpdateMutation);

    //#endregion

    async function onSubmit({ location, ...body }: Inputs) {
        if (updateFields) {
            const changed = changedFields(updateFields, body);
            if (changed) {
                await updateMutation.mutateAsync({
                    body: changed,
                    location,
                    uuid: updateFields.uuid,
                });
            }
        } else {
            await createMutation.mutateAsync({ body, location });
        }
    }

    const isEdit = !!item;

    return (
        <WAdminModalCrudForm entityType="machine" isUpdate={isEdit} form={form} onSubmit={onSubmit}>
            <UuidField value={item?.uuid} />

            <AdminLocationField headless locations={locations} isUpdate={isEdit} />

            <AFTextField headless control={control} name="label" isRequired config={machineItemFields.label} />

            <Controller
                control={control}
                name="os_id"
                render={(p) => (
                    <FormField wide headless label={machineItemFields.os.title} {...formPropsToFormFieldProps(p)}>
                        <AdminMetalOsSelect
                            defaultValueKey={p.field.value}
                            onChange={(item) => p.field.onChange(item?.os_id ?? null)}
                        />
                    </FormField>
                )}
            />

            <Controller
                control={control}
                name="spec_uuid"
                {...controllerProps({ isRequired: true })}
                render={(p) => (
                    <FormField
                        wide
                        headless
                        isRequired
                        label={machineItemFields.spec_title.title}
                        {...formPropsToFormFieldProps(p)}
                    >
                        <ASpecSelect
                            getKey={(i) => i.uuid}
                            defaultValueKey={p.field.value}
                            onChange={(spec) => {
                                p.field.onChange(spec.uuid);
                            }}
                        />
                    </FormField>
                )}
            />

            <AFTextField
                headless
                control={control}
                name="ip_public_v4"
                isRequired
                config={machineItemFields.ip_public_v4}
            />
            <AFTextField
                headless
                control={control}
                name="ip_public_v6"
                nullable
                config={machineItemFields.ip_public_v6}
            />

            <AFTextField headless control={control} name="admin_notes" config={machineItemFields.admin_notes}>
                <ATextArea />
            </AFTextField>

            <AFTextField
                headless
                control={control}
                name="ssh_credentials"
                nullable
                config={machineItemFields.ssh_credentials}
            >
                <ATextArea />
            </AFTextField>
        </WAdminModalCrudForm>
    );
}
